import { FC, MouseEvent, ReactElement, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
	Avatar,
	AwardRibbonOutline,
	BeakerOutline,
	BookmarkOutline,
	Box,
	BoxProps,
	ChartBarOutline,
	ChatAlt2Outline,
	ChatAltOutline,
	ChevronRightOutline,
	CogOutline,
	DotsHorizontalOutline,
	DownloadOutline,
	HistoryOutline,
	HomeOutline,
	HStack,
	LeftDrawerCollapseOutline,
	LeftDrawerExpandOutline,
	LifeRaftOutline,
	LogoutOutline,
	MailOutline,
	QuestionMarkCircleAltOutline,
	rem,
	ShoppingCartOutline,
	Stack,
	TargetOutline,
	Text,
	Tooltip,
	VStack,
} from '@bonusly/design-system';
import { useBuildMonarchURL } from '@bonusly/url';
import { Permission, useCurrentCompany, useCurrentUser, usePermission } from '@bonusly/who-am-i';

import { Reveal } from '../../../components/display/reveal';
import { useIsRecognize } from '../../../hooks/use-is-recognize';
import { AppPath } from '../../admin-navbar/config/router';
import { useFetchNotifications } from '../../top-navbar/components/Notification/hooks/useFetchNotifications';
import { useAdminNavLink } from '../hooks/use-admin-nav-link';
import { CompanyLogo } from './company-logo';
import { CreateNewMenu } from './create-new-menu';
import { NotificationMenuItem, NotificationPanel } from './notification';
import { SearchNavItem } from './search-nav-item';
import { TrialCard } from './trial-card';
import { UserNavDivider } from './user-nav-divider';
import { UserNavLink } from './user-nav-link';
import { UserNavLinkExternal } from './user-nav-link-external';
import { UserNavMenu } from './user-nav-menu';

// Sidebar widths matching AppLayout
const SIDEBAR_WIDTH_EXPANDED = 260;
const SIDEBAR_WIDTH_COLLAPSED = 56;

interface UserNavbarProps {
	isExpanded: boolean;
	toggleSidebar: () => void;
}

export const UserNavbar: FC<UserNavbarProps> = ({ isExpanded, toggleSidebar }) => {
	const { t } = useTranslation('navigation');
	const { t: c } = useTranslation('common');
	const isRecognize = useIsRecognize();
	const monarchURL = useBuildMonarchURL();
	const user = useCurrentUser();
	const isAdmin = user.global_admin;
	const canViewAdmin = usePermission(Permission.CanViewAdmin);
	const { hide_competitive_features: hideCompetitiveFeatures } = useCurrentCompany();
	const showLeaderboard = !(hideCompetitiveFeatures && !isAdmin);

	const homeURL = isRecognize ? monarchURL(AppPath.Home) : AppPath.Home;
	const adminNavLink = useAdminNavLink();
	const { unreadCount } = useFetchNotifications(user.locale);

	// Notification panel state
	const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
	const sidebarWidth = isExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;

	// Handle click for cross-domain navigation
	const handleLogoClick = (e: MouseEvent<HTMLAnchorElement>) => {
		if (isRecognize) {
			e.preventDefault();
			window.location.href = homeURL;
		}
	};

	const handleOpenNotifications = () => {
		setIsNotificationPanelOpen(true);
	};

	const handleCloseNotifications = () => {
		setIsNotificationPanelOpen(false);
	};

	return (
		<VStack bgColor="bg.canvas" w="full" h="100%" px={rem(8)} py={rem(20)} spacing={0} align="flex-start">
			<HStack justifyContent="center" height={rem(36)} width="100%" px={rem(10)} mb={rem(24)}>
				{isExpanded ? (
					<HStack justifyContent="space-between" alignItems="center" width="100%">
						<Box as={Link} id="primary-nav-logo" h={rem(36)} to={homeURL} onClick={handleLogoClick}>
							<CompanyLogo />
						</Box>
						<Tooltip label={t('collapseSidebar')} placement="right">
							<Box as="span" display="inline-flex">
								<LeftDrawerCollapseOutline
									data-testid="collapse-sidebar"
									cursor="pointer"
									onClick={toggleSidebar}
									size="md"
									color="text.softest"
									paddingInline={0}
								/>
							</Box>
						</Tooltip>
					</HStack>
				) : (
					<Tooltip label={t('expandSidebar')} placement="right">
						<Box as="span" display="inline-flex">
							<LeftDrawerExpandOutline data-testid="expand-sidebar" cursor="pointer" onClick={toggleSidebar} size="md" color="text.softest" paddingInline={0} />
						</Box>
					</Tooltip>
				)}
			</HStack>

			<Stack as="nav" spacing={1} flexDir="column" width="100%" flex={1} minHeight={0}>
				{/* HOME */}
				<UserNavLink to="/" isExpanded={isExpanded} data-testid="nav-link-home">
					<IconAndTextContent icon={<HomeOutline size="md" />} label={t('links.home')} isExpanded={isExpanded} />
				</UserNavLink>

				{/* SEARCH */}
				<SearchNavItem isExpanded={isExpanded} />

				{/* 1:1s */}
				<UserNavLink to="/growth/meet" isExpanded={isExpanded} data-testid="nav-link-one-on-ones">
					<IconAndTextContent icon={<ChatAlt2Outline size="md" />} label={t('links.oneOnOnes')} isExpanded={isExpanded} />
				</UserNavLink>

				{/* GOALS */}
				<UserNavLink to="/growth/goals" isExpanded={isExpanded} data-testid="nav-link-goals">
					<IconAndTextContent icon={<TargetOutline size="md" />} label={t('links.goals')} isExpanded={isExpanded} />
				</UserNavLink>

				{/* RECAPS */}
				<UserNavLink to="/growth/recap-review" isExpanded={isExpanded} data-testid="nav-link-recaps">
					<IconAndTextContent icon={<HistoryOutline size="md" />} label={t('links.recaps')} isExpanded={isExpanded} />
				</UserNavLink>

				{/* REWARDS */}
				<UserNavLink to="/rewards" isExpanded={isExpanded} data-testid="nav-link-rewards">
					<IconAndChildContent icon={<ShoppingCartOutline size="md" />} isExpanded={isExpanded}>
						<HStack justifyContent="space-between" alignItems="flex-start" width="100%">
							<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap">
								{t('links.rewards')}
							</Text>
							<Box bgColor="points.bgAlpha" fontWeight="semibold" fontSize={1} color="points.fg" borderRadius="full" px={rem(6)} py={rem(3)}>
								+{c('intl_number', { value: user.earning_balance })}
							</Box>
						</HStack>
					</IconAndChildContent>
				</UserNavLink>

				{/* TEAM NAV ITEMS */}

				{/* MORE MENU ITEMS */}
				<UserNavMenu buttonIcon={<DotsHorizontalOutline size="md" />} buttonText={t('moreButtonText')} isExpanded={isExpanded}>
					{/* INCENTIVES */}
					<UserNavLink to="/incentives" isExpanded data-testid="nav-link-incentives">
						<IconAndTextContent icon={<AwardRibbonOutline size="md" />} label={t('links.incentives')} isExpanded />
					</UserNavLink>
					{/* LEADERBOARD */}
					{/* NOTE: don't use Reveal/Conceal here because the Reveal/Conceal component returns a ReactElement which will cause the UserNavMenu to render an extra empty div */}
					{showLeaderboard && (
						<UserNavLink to="/analytics/leaderboards" isRecognizeRoute={true} isExpanded data-testid="nav-link-leaderboard">
							<IconAndTextContent icon={<ChartBarOutline size="md" />} label={t('links.leaderboard')} isExpanded />
						</UserNavLink>
					)}
					{/* FEEDBACK */}
					<UserNavLink to="/growth/feedback" isExpanded data-testid="nav-link-feedback">
						<IconAndTextContent icon={<ChatAltOutline size="md" />} label={t('links.feedback')} isExpanded />
					</UserNavLink>
				</UserNavMenu>

				{/* CREATE NEW MENU */}
				<CreateNewMenu isExpanded={isExpanded} />
				<VStack spacing={rem(8)} mt="auto">
					{/* TRIAL CARD */}
					<TrialCard isExpanded={isExpanded} />

					{/* ADMIN MENU */}
					{canViewAdmin && (
						<UserNavLink to={adminNavLink.to} isRecognizeRoute={adminNavLink.isRecognizeRoute} isExpanded={isExpanded} data-testid="nav-link-admin">
							<IconAndTextContent icon={<CogOutline size="md" />} label={t('links.adminSettings')} isExpanded={isExpanded} />
						</UserNavLink>
					)}

					{/* USER MENU */}
					<UserNavMenu
						buttonIcon={
							<Box position="relative" display="flex" alignItems="center">
								<Avatar name={user.display_name} src={user.profile_pic_url} h={rem(24)} w={rem(24)} fontSize={1} />
								{unreadCount > 0 && <Box position="absolute" top={rem(-4)} right={rem(-4)} h={rem(10)} w={rem(10)} bg="red.500" borderRadius="full" />}
							</Box>
						}
						buttonContents={
							<HStack justifyContent="space-between" alignItems="flex-start" width="100%" data-testid="user-profile-button">
								<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap">
									{user.display_name}
								</Text>
								<DotsHorizontalOutline size="md" />
							</HStack>
						}
						isExpanded={isExpanded}
					>
						{/* VIEW PROFILE */}
						<UserNavLink to={`/company/users/${user.id}`} isRecognizeRoute={true} isExpanded data-testid="nav-link-view-profile">
							<IconAndTextContent
								icon={<Avatar name={user.display_name} src={user.profile_pic_url} h={rem(24)} w={rem(24)} fontSize={1} />}
								label={t('user_menu.view_profile')}
								isExpanded
							/>
						</UserNavLink>

						{/* PROFILE SETTINGS */}
						<UserNavLink to="/users/edit" isRecognizeRoute={true} isExpanded data-testid="nav-link-profile-settings">
							<IconAndTextContent icon={<CogOutline size="md" />} label={t('user_menu.profile_settings')} isExpanded />
						</UserNavLink>

						{/* BOOKMARKS */}
						<UserNavLink to="/bookmark" isExpanded data-testid="nav-link-bookmarks">
							<IconAndTextContent icon={<BookmarkOutline size="md" />} label={t('user_menu.bookmarks')} isExpanded />
						</UserNavLink>

						{/* NOTIFICATIONS */}
						<NotificationMenuItem isExpanded onClick={handleOpenNotifications} />

						<UserNavDivider />

						{/* HELP SUBMENU */}
						<UserNavMenu
							buttonIcon={<QuestionMarkCircleAltOutline size="md" />}
							buttonContents={
								<HStack justifyContent="space-between" alignItems="center" width="100%">
									<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap">
										{t('user_menu.help')}
									</Text>
									<ChevronRightOutline size="sm" />
								</HStack>
							}
							isExpanded
							isSubmenu
						>
							{/* CONTACT US */}
							<UserNavLinkExternal href="https://bonusly.com/company/contact" data-testid="nav-link-contact-us">
								<IconAndTextContent icon={<MailOutline size="md" />} label={t('user_menu.contact')} isExpanded />
							</UserNavLinkExternal>

							{/* HELP CENTER */}
							<UserNavLinkExternal href="https://help.bonus.ly/en/" data-testid="nav-link-help-center">
								<IconAndTextContent icon={<LifeRaftOutline size="md" />} label={t('user_menu.helpCenter')} isExpanded />
							</UserNavLinkExternal>

							{/* WHAT'S NEW */}
							<UserNavLinkExternal href="https://help.bonus.ly/en/articles/4076250-product-updates-and-new-features" data-testid="nav-link-whats-new">
								<IconAndTextContent icon={<BeakerOutline size="md" />} label={t('user_menu.whatsNew')} isExpanded />
							</UserNavLinkExternal>

							<UserNavDivider />

							{/* ANDROID APP */}
							<UserNavLinkExternal href="https://play.google.com/store/apps/details?id=ly.bonus.android" data-testid="nav-link-android-app">
								<IconAndTextContent icon={<DownloadOutline size="md" />} label={t('user_menu.androidApp')} isExpanded />
							</UserNavLinkExternal>

							{/* iOS APP */}
							<UserNavLinkExternal href="https://apps.apple.com/us/app/bonusly/id995249036" data-testid="nav-link-ios-app">
								<IconAndTextContent icon={<DownloadOutline size="md" />} label={t('user_menu.iosApp')} isExpanded />
							</UserNavLinkExternal>

							<UserNavDivider />

							{/* STATUS PAGE */}
							<UserNavFooterLink href="https://status.bonus.ly/" mt={rem(8)} data-testid="nav-link-status-page">
								{t('user_menu.statusPage')}
							</UserNavFooterLink>

							{/* TERMS OF SERVICE */}
							<UserNavFooterLink href="https://bonusly.com/terms" data-testid="nav-link-terms-of-service">
								{t('user_menu.termsOfService')}
							</UserNavFooterLink>

							{/* PRIVACY POLICY */}
							<UserNavFooterLink href="https://bonus.ly/privacy_policy" mb={rem(8)} data-testid="nav-link-privacy-policy">
								{t('user_menu.privacyPolicy')}
							</UserNavFooterLink>
						</UserNavMenu>

						{/* LOG OUT */}
						<UserNavLink to="/users/sign_out" isRecognizeRoute={true} isExpanded data-testid="nav-link-logout">
							<IconAndTextContent icon={<LogoutOutline size="md" />} label={t('user_menu.log_out')} isExpanded />
						</UserNavLink>
					</UserNavMenu>
				</VStack>
			</Stack>

			{/* Notification Panel */}
			<NotificationPanel isOpen={isNotificationPanelOpen} onClose={handleCloseNotifications} sidebarWidth={sidebarWidth} />
		</VStack>
	);
};

type IconAndTextContentProps = {
	icon: ReactElement;
	label: string;
	isExpanded: boolean;
};

export const IconAndTextContent: FC<IconAndTextContentProps> = ({ icon, label, isExpanded }) => (
	<HStack justifyContent={isExpanded ? 'flex-start' : 'center'} alignItems={isExpanded ? 'flex-start' : 'center'} spacing={rem(12)}>
		{icon}
		<Reveal if={isExpanded}>
			<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap">
				{label}
			</Text>
		</Reveal>
	</HStack>
);

type IconAndChildContentProps = {
	icon: ReactElement;
	isExpanded: boolean;
	children: ReactNode;
};

const IconAndChildContent: FC<IconAndChildContentProps> = ({ icon, isExpanded, children }) => (
	<HStack justifyContent={isExpanded ? 'flex-start' : 'center'} alignItems={isExpanded ? 'flex-start' : 'center'}>
		{icon}
		<Reveal if={isExpanded}>{children}</Reveal>
	</HStack>
);

type UserNavFooterLinkProps = BoxProps & {
	children: ReactNode;
	href: string;
};

const UserNavFooterLink: FC<UserNavFooterLinkProps> = ({ children, href, ...props }) => (
	<Box as="a" href={href} w="100%" px={rem(12)} py={0} {...props}>
		<Text color="text.softest" fontSize={50} fontWeight="normal" _hover={{ textDecoration: 'underline' }}>
			{children}
		</Text>
	</Box>
);
